import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box, Typography, TextField, Button, Paper, IconButton,
  Switch, FormControlLabel, FormGroup
} from '@mui/material'
import { Add, Delete } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { apiClient } from '@/services/apiClient'
import { Discussion } from '@/types'
import MediaUploader, { UploadedMedia } from '@/components/MediaUploader'

export default function CreateDiscussion() {
  const { groupId } = useParams<{ groupId: string }>()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [mediaUploads, setMediaUploads] = useState<UploadedMedia[]>([])
  const [submitting, setSubmitting] = useState(false)

  // Poll state
  const [includePoll, setIncludePoll] = useState(false)
  const [pollOptions, setPollOptions] = useState<string[]>(['', ''])
  const [allowMultiple, setAllowMultiple] = useState(false)

  // Sign-up sheet state
  const [includeSignUp, setIncludeSignUp] = useState(false)
  const [signUpItems, setSignUpItems] = useState<string[]>([''])
  const [allowAddItems, setAllowAddItems] = useState(false)

  const handleAddOption = () => {
    setPollOptions([...pollOptions, ''])
  }

  const handleRemoveOption = (index: number) => {
    if (pollOptions.length <= 2) return
    setPollOptions(pollOptions.filter((_, i) => i !== index))
  }

  const handleOptionChange = (index: number, value: string) => {
    const updated = [...pollOptions]
    updated[index] = value
    setPollOptions(updated)
  }

  const validPollOptions = pollOptions.filter(o => o.trim().length > 0)
  const isPollValid = !includePoll || validPollOptions.length >= 2

  const handleAddSignUpItem = () => {
    setSignUpItems([...signUpItems, ''])
  }

  const handleRemoveSignUpItem = (index: number) => {
    if (signUpItems.length <= 1) return
    setSignUpItems(signUpItems.filter((_, i) => i !== index))
  }

  const handleSignUpItemChange = (index: number, value: string) => {
    const updated = [...signUpItems]
    updated[index] = value
    setSignUpItems(updated)
  }

  const validSignUpItems = signUpItems.filter(i => i.trim().length > 0)
  const isSignUpValid = !includeSignUp || validSignUpItems.length >= 1

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || !description.trim() || !groupId || !isPollValid || !isSignUpValid) return
    setSubmitting(true)
    try {
      const body: Record<string, unknown> = {
        title: title.trim(),
        description: description.trim(),
        mediaIds: mediaUploads.map(u => u.id),
      }
      if (includePoll) {
        body.poll = {
          allowMultipleSelections: allowMultiple,
          options: validPollOptions.map(o => o.trim()),
        }
      }
      if (includeSignUp) {
        body.signUpSheet = {
          allowAddItems,
          items: validSignUpItems.map(i => i.trim()),
        }
      }
      const discussion = await apiClient.post<Discussion>(`/api/discussions/group/${groupId}`, body)
      enqueueSnackbar('Discussion created', { variant: 'success' })
      navigate(`/discussions/${discussion.id}`)
    } catch {
      enqueueSnackbar('Failed to create discussion', { variant: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
          New Discussion
        </Typography>
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            margin="normal"
            required
            autoFocus
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            margin="normal"
            required
            multiline
            rows={4}
            slotProps={{ htmlInput: { maxLength: 5000 } }}
          />
          <Box sx={{ mt: 1 }}>
            <MediaUploader
              uploads={mediaUploads}
              onChange={setMediaUploads}
              disabled={submitting}
            />
          </Box>

          {/* Poll section */}
          <FormGroup sx={{ mt: 2 }}>
            <FormControlLabel
              control={<Switch checked={includePoll} onChange={e => setIncludePoll(e.target.checked)} />}
              label="Include a poll"
            />
          </FormGroup>

          {includePoll && (
            <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Poll Options</Typography>
              {pollOptions.map((option, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={`Option ${index + 1}`}
                    value={option}
                    onChange={e => handleOptionChange(index, e.target.value)}
                    slotProps={{ htmlInput: { maxLength: 500 } }}
                  />
                  {pollOptions.length > 2 && (
                    <IconButton size="small" onClick={() => handleRemoveOption(index)} color="error">
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button size="small" startIcon={<Add />} onClick={handleAddOption}>
                Add Option
              </Button>
              {!isPollValid && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  A poll requires at least two non-empty options.
                </Typography>
              )}
              <FormControlLabel
                control={<Switch checked={allowMultiple} onChange={e => setAllowMultiple(e.target.checked)} />}
                label="Allow selecting multiple options"
                sx={{ mt: 1, display: 'block' }}
              />
            </Paper>
          )}

          {/* Sign-up sheet section */}
          <FormGroup sx={{ mt: 2 }}>
            <FormControlLabel
              control={<Switch checked={includeSignUp} onChange={e => setIncludeSignUp(e.target.checked)} />}
              label="Include a sign-up sheet"
            />
          </FormGroup>

          {includeSignUp && (
            <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Items to Sign Up For</Typography>
              {signUpItems.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={`Item ${index + 1}`}
                    value={item}
                    onChange={e => handleSignUpItemChange(index, e.target.value)}
                    slotProps={{ htmlInput: { maxLength: 200 } }}
                  />
                  {signUpItems.length > 1 && (
                    <IconButton size="small" onClick={() => handleRemoveSignUpItem(index)} color="error">
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button size="small" startIcon={<Add />} onClick={handleAddSignUpItem}>
                Add Item
              </Button>
              {!isSignUpValid && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  A sign-up sheet requires at least one item.
                </Typography>
              )}
              <FormControlLabel
                control={<Switch checked={allowAddItems} onChange={e => setAllowAddItems(e.target.checked)} />}
                label="Allow people to add additional items"
                sx={{ mt: 1, display: 'block' }}
              />
            </Paper>
          )}

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button onClick={() => navigate(-1)} sx={{ flex: 1 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !title.trim() || !description.trim() || !isPollValid || !isSignUpValid}
              sx={{ flex: 1 }}
            >
              {submitting ? 'Creating...' : 'Create'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
